import { useEffect, useId, useMemo, useState, type CSSProperties } from 'react';
import { LIMITS } from '@shared/constants';
import type { CustomSymbol, Routine, RoutineKind } from '@shared/schemas';
import { useLanguage, useT } from '../i18n';
import { displaySymbolsFor, findDisplaySymbol, type DisplayCategory } from '../lib/board';
import { newId } from '../lib/ids';
import { Dialog } from './Dialog';
import { Notice } from './Notice';
import { Pictogram } from './Pictogram';

interface Props {
  open: boolean;
  routine: Routine | null;
  categories: DisplayCategory[];
  customSymbols: CustomSymbol[];
  onClose(): void;
  onSave(routine: Routine): void;
}

const FIRST_THEN_STEPS = 2;

/** Creates or edits a routine: a name, its type and the steps (symbols of the board). */
export function RoutineEditor({ open, routine, categories, customSymbols, onClose, onSave }: Props) {
  const t = useT();
  const language = useLanguage();
  const nameId = useId();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<RoutineKind>('sequence');
  const [steps, setSteps] = useState<string[]>([]);
  const [categoryId, setCategoryId] = useState<string>(categories[0]?.id ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(routine?.name ?? '');
    setKind(routine?.kind ?? 'sequence');
    setSteps(routine?.steps ?? []);
    setError(null);
    setCategoryId((id) => (categories.some((c) => c.id === id) ? id : (categories[0]?.id ?? '')));
  }, [open, routine, categories]);

  const max = kind === 'firstThen' ? FIRST_THEN_STEPS : LIMITS.maxRoutineSteps;
  const colorOf = useMemo(() => new Map(categories.map((c) => [c.id, c.color])), [categories]);
  const pickable = displaySymbolsFor(categoryId, customSymbols, language);
  const resolved = steps.map((id) => findDisplaySymbol(id, customSymbols, language));

  function changeKind(next: RoutineKind) {
    setKind(next);
    if (next === 'firstThen') setSteps((s) => s.slice(0, FIRST_THEN_STEPS));
  }

  function add(id: string) {
    setError(null);
    setSteps((s) => (s.length < max ? [...s, id] : s));
  }

  function moveStep(index: number, delta: -1 | 1) {
    setSteps((s) => {
      const target = index + delta;
      if (target < 0 || target >= s.length) return s;
      const copy = [...s];
      [copy[index], copy[target]] = [copy[target]!, copy[index]!];
      return copy;
    });
  }

  function submit() {
    const trimmed = name.trim();
    if (!trimmed) return setError(t('routineNameRequired'));
    const valid = steps.filter((id) => findDisplaySymbol(id, customSymbols, language));
    if (kind === 'firstThen' && valid.length !== FIRST_THEN_STEPS) return setError(t('routineFirstThenRequired'));
    if (!valid.length) return setError(t('routineStepsRequired'));
    onSave({ id: routine?.id ?? newId('rot'), name: trimmed.slice(0, LIMITS.maxRoutineNameLength), kind, steps: valid });
  }

  const pickHint =
    steps.length >= max
      ? t('routineFull')
      : kind === 'firstThen'
        ? steps.length === 0
          ? t('routinePickFirst')
          : t('routinePickThen')
        : t('routinePick');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      wide
      title={routine ? t('routineEdit') : t('routineNew')}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="btn btn--primary" onClick={submit}>
            {t('save')}
          </button>
        </>
      }
    >
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="field">
          <label htmlFor={nameId}>{t('routineName')}</label>
          <input
            id={nameId}
            value={name}
            maxLength={LIMITS.maxRoutineNameLength}
            placeholder={t('routineNamePlaceholder')}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <fieldset className="field routine-kind">
          <legend className="field__label">{t('routineKind')}</legend>
          <label className="check">
            <input type="radio" name="routine-kind" checked={kind === 'sequence'} onChange={() => changeKind('sequence')} />
            <span>
              <strong>🗓️ {t('routineSequence')}</strong>
              <span className="muted small">{t('routineSequenceHint', { n: LIMITS.maxRoutineSteps })}</span>
            </span>
          </label>
          <label className="check">
            <input type="radio" name="routine-kind" checked={kind === 'firstThen'} onChange={() => changeKind('firstThen')} />
            <span>
              <strong>➡️ {t('routineFirstThen')}</strong>
              <span className="muted small">{t('routineFirstThenHint')}</span>
            </span>
          </label>
        </fieldset>

        <div className="field">
          <span className="field__label">
            {t('routineSteps')} ({steps.length}/{max})
          </span>
          {steps.length === 0 ? (
            <p className="muted">{t('routineNoSteps')}</p>
          ) : (
            <ol className="step-list">
              {resolved.map((s, i) => (
                <li key={`${steps[i]}-${i}`} className="step-list__item">
                  <span className="step-list__number">{kind === 'firstThen' ? (i === 0 ? t('first') : t('then')) : i + 1}</span>
                  {s ? <Pictogram source={s.picto} className="step-list__picto" /> : <span className="step-list__picto">❔</span>}
                  <span className="step-list__label">{s?.label ?? '—'}</span>
                  <span className="step-list__tools">
                    <button type="button" className="icon-btn" onClick={() => moveStep(i, -1)} disabled={i === 0} aria-label={t('moveUp')}>
                      ▲
                    </button>
                    <button type="button" className="icon-btn" onClick={() => moveStep(i, 1)} disabled={i === steps.length - 1} aria-label={t('moveDown')}>
                      ▼
                    </button>
                    <button
                      type="button"
                      className="icon-btn icon-btn--danger"
                      onClick={() => setSteps((list) => list.filter((_, j) => j !== i))}
                      aria-label={t('removeStep', { n: i + 1 })}
                    >
                      ✕
                    </button>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="field">
          <span className="field__label">{pickHint}</span>
          <div className="picker__cats" role="group" aria-label={t('categoriesNav')}>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                className="chip chip--link"
                style={{ '--accent': c.color } as CSSProperties}
                aria-pressed={c.id === categoryId}
                onClick={() => setCategoryId(c.id)}
              >
                <Pictogram source={{ icon: c.icon, image: c.image }} className="chip__picto" />
                {c.name}
              </button>
            ))}
          </div>
          <div className="picker__grid">
            {pickable.map((s) => (
              <button
                key={s.id}
                type="button"
                className="picker__tile"
                style={{ '--accent': colorOf.get(s.categoryId) ?? '#64748b' } as CSSProperties}
                onClick={() => add(s.id)}
                disabled={steps.length >= max}
              >
                <Pictogram source={s.picto} className="picker__picto" />
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        </div>
        {error && <Notice kind="error">{error}</Notice>}
      </form>
    </Dialog>
  );
}
